import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { theme } from "../../theme"; 
import axios from "axios";
import { 
    Box, Button, Container, Table, Pagination, TableBody, TableCell, 
    TableHead, TableRow, TextField, Card, Typography, 
    Stack, Chip, TableContainer, CircularProgress, Collapse,
    Dialog, DialogTitle, DialogContent, DialogActions 
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
    Search as SearchIcon, 
    FileDownloadOutlined as DownloadIcon, 
    WarningAmber as WarningIcon,
    KeyboardArrowDown,
    KeyboardArrowUp,
    DeleteOutline as DeleteIcon,
    DeleteForever as DeleteForeverIcon
} from "@mui/icons-material";
import swal from "sweetalert";
import { t } from "i18next";

const minimalistTheme = createTheme({
    palette: {
        background: { default: "#F9F9F9", paper: "#FFFFFF" }, 
        text: { primary: "#181C32", secondary: "#A1A5B7" }, 
        primary: { main: "#2563EB", light: "#EFF6FF" }, 
        success: { main: "#16A34A", light: "#DCFCE7" }, 
        danger: { main: "#EF4444", light: "#FEE2E2" },
        warning: { main: "#F59E0B", light: "#FEF3C7" }, 
    },
    typography: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
    components: {
        MuiCard: {
            styleOverrides: {
                root: { borderRadius: "12px", border: "1px solid #EFF2F5", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.02)" }
            }
        },
        MuiTable: {
            styleOverrides: {
                root: { border: "none !important", borderCollapse: "collapse !important", backgroundColor: "transparent !important", tableLayout: "fixed" }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: { 
                    borderBottom: "1px solid #EFF2F5 !important", 
                    padding: "12px 8px", 
                    color: "#3F4254", 
                    fontSize: "0.85rem",
                    fontFamily: "'Plus Jakarta Sans', sans-serif !important",
                    wordWrap: "break-word",
                    borderTop: "none !important", borderLeft: "none !important", borderRight: "none !important", backgroundColor: "transparent !important",
                }
            }
        },
        MuiButton: {
            styleOverrides: { root: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }
        }
    }
});

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (config.headers) {
            if (token) config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

function Users() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    
    // TRẠNG THÁI CHO CHECKBOX TICK CHỌN USER
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const [showSuspicious, setShowSuspicious] = useState(false);
    const [suspiciousGroups, setSuspiciousGroups] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});

    // STATE CHO POPUP SỬA VÒNG CƯỢC
    const [openBetModal, setOpenBetModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [betMoneyInput, setBetMoneyInput] = useState("");
    const [betRoundInput, setBetRoundInput] = useState("");

    // STATE CHO POPUP DỌN DỮ LIỆU CHUNG
    const [openCleanModal, setOpenCleanModal] = useState(false);
    const [cleanRangeType, setCleanRangeType] = useState('month');
    const [cleanStartDate, setCleanStartDate] = useState('');
    const [cleanEndDate, setCleanEndDate] = useState('');

    const navigate = useNavigate();
    const typingTimeoutRef = useRef(null);
    const limit = 10;

    const fetchUsers = () => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/auth/getall`, {
            params: { page: page, limit: limit, search: searchQuery }
        }).then((res) => {
            setUsers(res.data.data || []);
            setTotalPages(res.data.totalPages || 1);
            setLoading(false);
            setSelectedUserIds([]);
        }).catch((err) => {
            console.error(err);
            setLoading(false);
        });
    };

    const fetchSuspiciousUsers = () => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/auth/getall`, {
            params: { page: 1, limit: 10000, search: "" } 
        }).then((res) => {
            const allData = res.data.data || [];
            const ipMap = {};
            allData.forEach(item => {
                if(item.ip && item.ip.trim() !== "") {
                    if(!ipMap[item.ip]) ipMap[item.ip] = [];
                    ipMap[item.ip].push(item);
                }
            });
            const groups = Object.values(ipMap).filter(group => group.length > 1);
            groups.sort((a, b) => b.length - a.length);
            setSuspiciousGroups(groups);
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    };

    const refreshData = () => {
        if (showSuspicious) fetchSuspiciousUsers();
        fetchUsers();
    };

    const toggleGroup = (ip) => {
        setExpandedGroups(prev => ({ ...prev, [ip]: !prev[ip] }));
    };

    useEffect(() => {
        fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchQuery]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            setSearchQuery(value);
            setPage(1); 
        }, 500);
    };

    const handleChangePage = (e, newPage) => setPage(newPage);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUserIds(users.map(u => u._id));
        } else {
            setSelectedUserIds([]);
        }
    };

    const handleSelectOne = (id) => {
        setSelectedUserIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(userId => userId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleDeleteSelectedUsers = () => {
        swal({
            title: "Xóa tài khoản?",
            text: `Bạn chuẩn bị xóa vĩnh viễn ${selectedUserIds.length} tài khoản khách hàng. Dữ liệu sẽ không thể khôi phục!`,
            icon: "warning",
            buttons: ["Hủy", "Xác nhận Xóa"],
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                setLoading(true);
                try {
                    const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/cleandata`, {
                        type: 'selected', 
                        userIds: selectedUserIds
                    });
                    swal("Thành công!", res.data.message, "success");
                    setSelectedUserIds([]);
                    refreshData();
                } catch (error) {
                    swal("Lỗi", "Không thể xóa tài khoản!", "error");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleOpenBetModal = (user) => {
        setSelectedUser(user);
        setBetMoneyInput(user?.tiencancuoc || 0); 
        setBetRoundInput(user?.soluongcancuoc || 0); 
        setOpenBetModal(true);
    };

    const handleCloseBetModal = () => {
        setOpenBetModal(false);
        setSelectedUser(null);
    };

    const handleSubmitBetUpdate = async () => {
        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/auth/update`, {
                id: selectedUser._id,
                type: '3', 
                tiencancuoc: Number(betMoneyInput), 
                soluongcancuoc: Number(betRoundInput) 
            });
            swal(t("success"), "Cập nhật yêu cầu cược thành công!", "success");
            handleCloseBetModal();
            refreshData();
        } catch (error) {
            swal("Lỗi", "Không thể cập nhật!", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCleanDataRange = async () => {
        if (cleanRangeType === 'custom' && (!cleanStartDate || !cleanEndDate)) {
            return swal("Cảnh báo", "Vui lòng chọn đầy đủ Ngày bắt đầu và Ngày kết thúc!", "warning");
        }

        swal({
            title: "Cảnh báo nguy hiểm!",
            text: "Hành động này sẽ XÓA VĨNH VIỄN các tài khoản khách hàng trong khoảng thời gian đã chọn. Bạn chắc chắn muốn tiếp tục?",
            icon: "warning",
            buttons: ["Hủy bỏ", "Xóa Dữ Liệu"],
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                setLoading(true);
                try {
                    const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/cleandata`, {
                        type: 'range', 
                        rangeType: cleanRangeType,
                        startDate: cleanStartDate,
                        endDate: cleanEndDate
                    });
                    swal("Thành công!", res.data.message || "Đã dọn dẹp dữ liệu.", "success");
                    setOpenCleanModal(false);
                    refreshData();
                } catch (error) {
                    swal("Lỗi", "Có lỗi xảy ra khi dọn dẹp dữ liệu!", "error");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    function formatDate(m) {
        if (!m) return "";
        const d = new Date(m);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${("0" + d.getDate()).slice(-2)} ${months[d.getMonth()]}, ${d.getFullYear()}`; 
    }

    const inputStyle = {
        height: '42px', padding: '0 12px', border: '1px solid #E4E6EF', 
        borderRadius: '8px', outline: 'none', fontSize: '0.85rem', fontWeight: '600',
        fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#181C32',
        transition: 'all 0.2s ease', backgroundColor: '#FFFFFF',
        width: '100%', minWidth: 0, boxSizing: 'border-box'
    };

    const tableHeadSx = {
        color: "#A1A5B7 !important", fontWeight: "700 !important", 
        fontSize: "0.75rem !important", backgroundColor: "#FFFFFF !important", 
        borderBottom: "2px solid #EFF2F5 !important",
        textTransform: "uppercase !important", whiteSpace: "nowrap"
    };

    const renderTableHeaders = () => (
        <>
            <TableCell sx={{ ...tableHeadSx, width: '3%', paddingLeft: '24px' }}>
                <input 
                    type="checkbox" 
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2563EB' }}
                    checked={selectedUserIds.length === users.length && users.length > 0}
                    onChange={handleSelectAll}
                />
            </TableCell>
            {/* Đảo ID lên trước TÁC VỤ */}
            <TableCell sx={{ ...tableHeadSx, width: '4%' }}>ID</TableCell>
            <TableCell align="center" sx={{ ...tableHeadSx, width: '10%' }}>TÁC VỤ</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '13%' }}>TÀI KHOẢN & NGÂN HÀNG</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '8%' }}>{t("balance")}</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '13%' }}>{t("with_reg")}</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '13%' }}>{t("reward")}</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '7%' }}>SĐT</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '13%' }}>TIẾN ĐỘ CƯỢC</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '7%' }}>IP</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: '9%', paddingRight: '24px' }}>NGÀY TẠO ↓</TableCell>
        </>
    );

    const renderUserRow = (item) => {
        const targetMoney = item?.tiencancuoc || 0;
        const currentMoney = item?.totalbet || 0;
        const isMoneyMet = currentMoney >= targetMoney;
        const moneyColor = targetMoney > 0 ? (isMoneyMet ? '#16A34A' : '#EF4444') : '#181C32'; 

        const targetRound = item?.soluongcancuoc || 0; 
        const currentRound = item?.tongluotcuoc || 0;  
        const isRoundMet = currentRound >= targetRound;
        const roundColor = targetRound > 0 ? (isRoundMet ? '#16A34A' : '#EF4444') : '#181C32';

        return (
            <TableRow key={item._id} hover sx={{ verticalAlign: "top", backgroundColor: selectedUserIds.includes(item._id) ? '#EFF6FF' : 'transparent' }}>
                <TableCell sx={{ pt: 2.5, paddingLeft: '24px' }}>
                    <input 
                        type="checkbox" 
                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2563EB' }}
                        checked={selectedUserIds.includes(item._id)}
                        onChange={() => handleSelectOne(item._id)}
                    />
                </TableCell>
                
                {/* CỘT ID đảo lên trước */}
                <TableCell sx={{ fontWeight: 600, pt: 2.5 }}>{item?.iduser}</TableCell>
                
                {/* CỘT TÁC VỤ (Nút bấm) */}
                <TableCell align="center" sx={{ pt: 2.5 }}>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => navigate(`/admin/user/${item?._id}`)}
                            sx={{ 
                                minWidth: '50px', px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 700, borderRadius: '6px',
                                borderColor: '#2563EB', color: '#2563EB', textTransform: 'none',
                                '&:hover': { backgroundColor: '#EFF6FF' }
                            }}
                        >
                            Xem
                        </Button>
                        <Button 
                            variant={item?.isLock ? "contained" : "outlined"}
                            color={item?.isLock ? "success" : "error"}
                            size="small"
                            onClick={() => {
                                setLoading(true);
                                axios.post(`${process.env.REACT_APP_API_URL}/auth/lockkey`, { id: item?._id, isLock: !item?.isLock })
                                    .then(() => { refreshData(); swal(t("success"), "", "success"); })
                                    .catch(() => setLoading(false));
                            }}
                            sx={{ 
                                minWidth: '60px', px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 700, borderRadius: '6px', textTransform: 'none',
                                borderColor: !item?.isLock ? '#EF4444' : undefined, color: !item?.isLock ? '#EF4444' : '#FFF',
                                '&:hover': { backgroundColor: !item?.isLock ? '#FEF2F2' : undefined }
                            }}
                        >
                            {item?.isLock ? "Mở khóa" : "Khóa"}
                        </Button>
                    </Stack>
                </TableCell>
                
                {/* CỘT TÀI KHOẢN & NGÂN HÀNG (Box bọc ngân hàng) */}
                <TableCell sx={{ pt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: item?.isLock ? '#A1A5B7' : '#2563EB', mb: 0.5, fontSize: '0.9rem', display: 'flex', alignItems: 'center' }}>
                        {item?.username} 
                        {item?.isLock && <Chip label="Đã khóa" size="small" sx={{ ml: 1, height: '18px', fontSize: '0.65rem', backgroundColor: '#FEE2E2', color: '#EF4444', fontWeight: 700 }} />}
                    </Typography>
                    
                    <Box sx={{ 
                        mt: 1, p: 1, 
                        backgroundColor: '#F9F9F9', 
                        borderRadius: '6px', 
                        border: '1px dashed #E4E6EF' 
                    }}>
                        <Typography variant="caption" fontWeight="800" color="#EF4444" display="block" sx={{ textTransform: 'uppercase', mb: 0.2, fontSize: '0.65rem' }}>
                            {item?.name_bank || 'CHƯA CẬP NHẬT'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.2, fontSize: '0.7rem', fontWeight: 600 }}>
                            {item?.bank || '---'}
                        </Typography>
                        <Typography variant="caption" fontWeight="800" color="#181C32" sx={{ fontSize: '0.75rem' }}>
                            {item?.stk_bank || '---'}
                        </Typography>
                    </Box>
                </TableCell>

                <TableCell sx={{ fontWeight: 800, color: '#16A34A', pt: 2.5, whiteSpace: 'nowrap' }}>{item?.money?.toLocaleString("vi-VN")} đ</TableCell>
                
                <TableCell sx={{ pt: 2 }}>
                    <form onSubmit={(e) => {
                        e.preventDefault(); setLoading(true);
                        const isSubtract = e.target.action.value === 'sub';
                        const amountValue = Math.abs(Number(e.target.money.value));
                        const finalMoney = isSubtract ? -amountValue : amountValue;

                        axios.post(`${process.env.REACT_APP_API_URL}/auth/update`, {
                            id: item?._id, money: finalMoney, type: '0' 
                        }).then(() => { refreshData(); swal(t("success"), "", "success"); e.target.reset(); }).catch(() => setLoading(false));
                    }}>
                        <Stack spacing={1}>
                            <Box sx={{ display: 'flex', gap: 1 }}> 
                                <select name="action" className="custom-input-metronic" style={{ ...inputStyle, width: '85px', padding: '0 8px', cursor: 'pointer', textAlign: 'center', fontWeight: '800', color: '#2563EB', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE'}}>
                                    <option value="add">Cộng</option>
                                    <option value="sub">Trừ</option>
                                </select>
                                <Box sx={{ flex: 1 }}>
                                    <input className="custom-input-metronic" style={{...inputStyle, minWidth: '85px'}} name="money" type="number" min="0" placeholder="Số lượng" required />
                                </Box>
                                <Button type="submit" variant="contained" size="small" sx={{ minWidth: '42px', height: '42px', p: 0, borderRadius: '8px', boxShadow: 'none', fontWeight: 800 }}>OK</Button>
                            </Box>
                        </Stack>
                    </form>
                </TableCell>

                <TableCell sx={{ pt: 2 }}>
                    <form onSubmit={(e) => {
                        e.preventDefault(); setLoading(true);
                        axios.post(`${process.env.REACT_APP_API_URL}/auth/adminthuong`, {
                            id: item?._id, money: Number(e.target.money.value), detail: e.target.detail.value
                        }).then(() => { refreshData(); swal(t("success"), "", "success"); e.target.reset(); }).catch(() => setLoading(false));
                    }}>
                        <Stack spacing={1}>
                            <input className="custom-input-metronic" name="detail" type="text" placeholder="Lý do..." style={inputStyle} />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                    <input className="custom-input-metronic" name="money" type="number" min="0" placeholder="Số tiền" style={{...inputStyle, minWidth: '85px'}} required />
                                </Box>
                                <Button type="submit" variant="contained" color="success" size="small" sx={{ minWidth: '42px', height: '42px', p: 0, borderRadius: '8px', boxShadow: 'none', fontWeight: 800 }}>OK</Button>
                            </Box>
                        </Stack>
                    </form>
                </TableCell>

                <TableCell sx={{ fontWeight: 600, pt: 2.5, whiteSpace: 'nowrap' }}>{item?.sdt ? "0" + item?.sdt : "-"}</TableCell>
                
                <TableCell sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#7E8299', fontWeight: 600 }}>Tiền cược:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                <span style={{ color: moneyColor }}>{currentMoney.toLocaleString("vi-VN")}</span>
                                <span style={{ color: '#A1A5B7', margin: '0 4px' }}>/</span>
                                <span style={{ color: '#181C32' }}>{targetMoney.toLocaleString("vi-VN")}</span>
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#7E8299', fontWeight: 600 }}>Số lượt:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                <span style={{ color: roundColor }}>{currentRound}</span>
                                <span style={{ color: '#A1A5B7', margin: '0 4px' }}>/</span>
                                <span style={{ color: '#181C32' }}>{targetRound}</span>
                            </Typography>
                        </Box>
                        <Typography 
                            component="span" onClick={() => handleOpenBetModal(item)}
                            sx={{ color: '#F59E0B', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right', display: 'block', mt: 0.5, '&:hover': { textDecoration: 'underline', color: '#D97706' } }}
                        >
                            + Sửa Cược
                        </Typography>
                    </Box>
                </TableCell>
                
                <TableCell sx={{ pt: 2.5 }}>
                    <Chip 
                        label={item?.ip || "N/A"} size="small" 
                        sx={{ height: '24px', fontSize: '0.75rem', fontWeight: 700, borderRadius: '4px', backgroundColor: showSuspicious ? '#FEE2E2' : '#F5F8FA', color: showSuspicious ? '#EF4444' : '#A1A5B7' }}
                    />
                </TableCell>
                
                <TableCell sx={{ color: '#7E8299', pt: 2.5, paddingRight: '24px', fontSize: '0.8rem', whiteSpace: 'nowrap', fontWeight: 600 }}>{formatDate(item?.createdAt)}</TableCell>
            </TableRow>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <ThemeProvider theme={minimalistTheme}>
                    <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                        .safe-users-zone * { box-sizing: border-box; font-family: 'Plus Jakarta Sans', sans-serif !important; }
                        .custom-input-metronic:focus { border-color: #2563EB !important; background-color: #FFFFFF !important; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }
                    `}</style>

                    <Box className="safe-users-zone" component="main" sx={{ flexGrow: 1, py: 4, backgroundColor: 'background.default', minHeight: 'calc(100vh - 70px)' }}>
                        {loading && (
                            <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CircularProgress size={40} sx={{ color: '#2563EB' }} />
                            </Box>
                        )}

                        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#181C32', mb: 0.5 }}>
                                        Danh Sách Khách Hàng
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#7E8299', fontWeight: 500 }}>
                                        Quản lý tập trung thông tin, giao dịch và phân quyền người dùng
                                    </Typography>
                                </Box>
                                
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    {selectedUserIds.length > 0 && (
                                        <Button 
                                            variant="contained" 
                                            startIcon={<DeleteForeverIcon />}
                                            onClick={handleDeleteSelectedUsers}
                                            sx={{ 
                                                textTransform: 'none', fontWeight: 800, borderRadius: '6px', 
                                                backgroundColor: '#DC2626', color: '#FFF', boxShadow: 'none',
                                                animation: 'pulse 2s infinite',
                                                '@keyframes pulse': {
                                                    '0%': { boxShadow: '0 0 0 0 rgba(220, 38, 38, 0.4)' },
                                                    '70%': { boxShadow: '0 0 0 10px rgba(220, 38, 38, 0)' },
                                                    '100%': { boxShadow: '0 0 0 0 rgba(220, 38, 38, 0)' }
                                                },
                                                '&:hover': { backgroundColor: '#B91C1C' }
                                            }}
                                        >
                                            Xóa {selectedUserIds.length} User
                                        </Button>
                                    )}

                                    <Button 
                                        variant="outlined" 
                                        startIcon={<DeleteIcon />}
                                        onClick={() => setOpenCleanModal(true)}
                                        sx={{ 
                                            textTransform: 'none', fontWeight: 700, borderRadius: '6px', 
                                            borderColor: '#E4E6EF', color: '#EF4444', backgroundColor: '#FFF',
                                            '&:hover': { backgroundColor: '#FEF2F2', borderColor: '#FCA5A5' }
                                        }}
                                    >
                                        Dọn Dữ Liệu
                                    </Button>

                                    <Button 
                                        variant={showSuspicious ? "contained" : "outlined"} color={showSuspicious ? "error" : "primary"}
                                        onClick={() => {
                                            if (!showSuspicious) fetchSuspiciousUsers();
                                            else setExpandedGroups({});
                                            setShowSuspicious(!showSuspicious);
                                        }}
                                        sx={{ 
                                            textTransform: 'none', fontWeight: 700, borderRadius: '6px', 
                                            boxShadow: showSuspicious ? '0px 4px 12px rgba(239, 68, 68, 0.3)' : '0px 2px 5px rgba(0,0,0,0.02)',
                                            borderColor: showSuspicious ? '#EF4444' : '#E4E6EF', color: showSuspicious ? '#FFF' : '#3F4254',
                                            backgroundColor: showSuspicious ? '#EF4444' : '#FFF',
                                            '&:hover': { backgroundColor: showSuspicious ? '#DC2626' : '#F9F9F9', borderColor: showSuspicious ? '#DC2626' : '#E4E6EF' }
                                        }}
                                    >
                                        {showSuspicious ? "Hủy Check (Quay lại)" : "Check Ghi Vấn IP"}
                                    </Button>
                                </Stack>
                            </Box>

                            {showSuspicious ? (
                                <Box>
                                    {suspiciousGroups.length > 0 ? (
                                        suspiciousGroups.map((group, index) => {
                                            const ip = group[0].ip;
                                            const isExpanded = !!expandedGroups[ip];

                                            return (
                                                <Card key={index} sx={{ mb: 3, border: '1px solid #FCA5A5', boxShadow: '0px 4px 20px rgba(239, 68, 68, 0.05)', overflow: 'hidden' }}>
                                                    <Box onClick={() => toggleGroup(ip)} sx={{ p: 2, backgroundColor: isExpanded ? '#FEF2F2' : '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { backgroundColor: '#FEF2F2' }}}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <WarningIcon sx={{ color: '#EF4444' }} />
                                                            <Typography variant="subtitle1" fontWeight="800" color="#B91C1C">Phát hiện Trùng IP: {ip}</Typography>
                                                            <Chip label={`${group.length} tài khoản`} size="small" sx={{ backgroundColor: '#EF4444', color: '#FFF', fontWeight: '800' }} />
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="700" color="#EF4444" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {isExpanded ? 'Thu gọn' : 'Xem chi tiết'} {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <TableContainer sx={{ overflowX: 'auto', borderTop: '1px dashed #FCA5A5' }}>
                                                            <Table sx={{ width: '100%' }}> 
                                                                <TableHead><TableRow>{renderTableHeaders()}</TableRow></TableHead>
                                                                <TableBody>{group.map(item => renderUserRow(item))}</TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </Collapse>
                                                </Card>
                                            );
                                        })
                                    ) : (
                                        <Box sx={{ py: 8, textAlign: 'center', backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #EFF2F5' }}>
                                            <Typography variant="h6" color="text.secondary" fontWeight="700">Tuyệt vời! Không phát hiện tài khoản nào trùng IP trong hệ thống.</Typography>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Card>
                                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #EFF2F5' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#181C32' }}>Dữ liệu người dùng</Typography>
                                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                                            <TextField 
                                                onChange={handleSearchChange} placeholder="Tìm kiếm user..." size="small"
                                                InputProps={{
                                                    startAdornment: <SearchIcon sx={{ color: '#A1A5B7', mr: 1, fontSize: '1.2rem' }} />,
                                                    sx: { borderRadius: '6px', backgroundColor: '#FFFFFF', '& fieldset': { borderColor: '#E4E6EF' }, minWidth: '220px', fontSize: '0.85rem', fontWeight: '500' }
                                                }}
                                            />
                                            <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderColor: '#E4E6EF', color: '#3F4254', textTransform: 'none', fontWeight: 700, borderRadius: '6px', padding: '5px 12px' }}>Xuất Excel</Button>
                                        </Stack>
                                    </Box>

                                    <TableContainer sx={{ overflowX: 'auto', border: 'none !important', borderRadius: '0 0 12px 12px' }}>
                                        <Table sx={{ width: '100%', '& thead, & thead tr, & thead th': { background: 'transparent !important', backgroundColor: '#FFFFFF !important', border: 'none !important' }}}>
                                            <TableHead><TableRow>{renderTableHeaders()}</TableRow></TableHead>
                                            <TableBody>
                                                {users && users.length > 0 ? ( users.map(item => renderUserRow(item)) ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={11} align="center" sx={{ py: 6, color: '#A1A5B7', borderBottom: 'none !important', fontWeight: '600' }}>
                                                            Không có dữ liệu.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: '1px solid #EFF2F5' }}>
                                        <Pagination color="primary" shape="rounded" size="small" count={totalPages} page={page} onChange={handleChangePage} sx={{ '& .MuiPaginationItem-root': { fontWeight: 700 } }} />
                                    </Box>
                                </Card>
                            )}

                            {/* POPUP: MODAL SỬA VÒNG CƯỢC / LƯỢT CƯỢC */}
                            <Dialog open={openBetModal} onClose={handleCloseBetModal} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
                                <DialogTitle sx={{ fontWeight: 800, color: '#181C32', pb: 1 }}>Thiết lập Yêu Cầu Cược</DialogTitle>
                                <DialogContent>
                                    <Typography variant="body2" sx={{ color: '#7E8299', mb: 3, fontWeight: 500 }}>
                                        Tài khoản: <strong style={{color: '#2563EB'}}>{selectedUser?.username}</strong>
                                    </Typography>
                                    <Box sx={{ mb: 2.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#3F4254', mb: 1, display: 'block' }}>Tiền vòng cược yêu cầu</Typography>
                                        <input type="number" className="custom-input-metronic" value={betMoneyInput} onChange={(e) => setBetMoneyInput(e.target.value)} style={inputStyle} placeholder="Ví dụ: 1000000" />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#3F4254', mb: 1, display: 'block' }}>Số lượt cược yêu cầu</Typography>
                                        <input type="number" className="custom-input-metronic" value={betRoundInput} onChange={(e) => setBetRoundInput(e.target.value)} style={inputStyle} placeholder="Ví dụ: 5" />
                                    </Box>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, pb: 2 }}>
                                    <Button onClick={handleCloseBetModal} sx={{ color: '#7E8299', fontWeight: 700, textTransform: 'none' }}>Hủy bỏ</Button>
                                    <Button onClick={handleSubmitBetUpdate} variant="contained" sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '6px', boxShadow: 'none' }}>Lưu thay đổi</Button>
                                </DialogActions>
                            </Dialog>

                            {/* POPUP: MODAL DỌN DẸP DỮ LIỆU THEO THỜI GIAN */}
                            <Dialog open={openCleanModal} onClose={() => setOpenCleanModal(false)} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
                                <DialogTitle sx={{ fontWeight: 800, color: '#B91C1C', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DeleteIcon /> Dọn Dẹp Dữ Liệu Hệ Thống
                                </DialogTitle>
                                <DialogContent>
                                    <Typography variant="body2" sx={{ color: '#7E8299', mb: 3, fontWeight: 500 }}>
                                        Hành động này sẽ xóa các tài khoản khách hàng, lịch sử giao dịch và biến động số dư theo khoảng thời gian để giảm tải cho máy chủ.
                                    </Typography>
                                    
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#3F4254', mb: 1.5, display: 'block' }}>
                                            Chọn mốc thời gian muốn lọc & xóa:
                                        </Typography>
                                        <select 
                                            className="custom-input-metronic" 
                                            style={{ ...inputStyle, cursor: 'pointer' }}
                                            value={cleanRangeType}
                                            onChange={(e) => setCleanRangeType(e.target.value)}
                                        >
                                            <option value="week">Dọn dữ liệu 7 ngày qua</option>
                                            <option value="month">Dọn dữ liệu 30 ngày qua</option>
                                            <option value="custom">Tùy chỉnh khoảng thời gian (Ngày - Ngày)</option>
                                        </select>
                                    </Box>

                                    {cleanRangeType === 'custom' && (
                                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#3F4254', mb: 1, display: 'block' }}>Từ ngày</Typography>
                                                <input 
                                                    type="date" 
                                                    className="custom-input-metronic" 
                                                    style={inputStyle}
                                                    value={cleanStartDate}
                                                    onChange={(e) => setCleanStartDate(e.target.value)}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#3F4254', mb: 1, display: 'block' }}>Đến ngày</Typography>
                                                <input 
                                                    type="date" 
                                                    className="custom-input-metronic" 
                                                    style={inputStyle}
                                                    value={cleanEndDate}
                                                    onChange={(e) => setCleanEndDate(e.target.value)}
                                                />
                                            </Box>
                                        </Stack>
                                    )}

                                    <Box sx={{ p: 1.5, backgroundColor: '#FEF2F2', borderRadius: '6px', border: '1px dashed #FCA5A5' }}>
                                        <Typography variant="caption" fontWeight="700" color="#DC2626" sx={{ display: 'flex', gap: 0.5 }}>
                                            <WarningIcon fontSize="small" sx={{ fontSize: '1rem' }} /> Cảnh báo: Dữ liệu đã xóa không thể khôi phục lại!
                                        </Typography>
                                    </Box>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, pb: 2 }}>
                                    <Button onClick={() => setOpenCleanModal(false)} sx={{ color: '#7E8299', fontWeight: 700, textTransform: 'none' }}>
                                        Hủy bỏ
                                    </Button>
                                    <Button 
                                        onClick={handleCleanDataRange} 
                                        variant="contained" 
                                        sx={{ 
                                            fontWeight: 700, textTransform: 'none', borderRadius: '6px', boxShadow: 'none',
                                            backgroundColor: '#EF4444', color: '#FFF',
                                            '&:hover': { backgroundColor: '#DC2626' }
                                        }}
                                    >
                                        Xác nhận Lọc & Xóa
                                    </Button>
                                </DialogActions>
                            </Dialog>

                        </Container>
                    </Box>
                </ThemeProvider>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default Users;